import { SerializedStyles } from '@emotion/react'
import React from 'react'

import { RadioButton } from 'components/common/RadioButton'
import Text from 'components/common/Text'
import theme from 'theme'

type Option = {
  name?: string
  value?: string
  disabled?: boolean
  description?: string
}

interface RadioFieldsetProps {
  className?: string
  title?: string
  value?: string | boolean
  options: Option[]
  onChange: (...args: any[]) => void
  fieldsStyle?: Record<string, SerializedStyles>
  fontSize?: number
  showValues?: boolean
}

const RadioFieldset: React.FC<RadioFieldsetProps> = ({
  className = '',
  title,
  value,
  options,
  onChange,
  fieldsStyle = {},
  fontSize = theme.fontSizes[1],
  showValues = true,
  ...rest
}) => {
  return (
    <fieldset
      data-test-radio-fieldset
      className={className}
      css={{ border: 0, margin: 0, padding: 0 }}
    >
      <legend
        css={{
          marginBottom: theme.space[3],
          ...fieldsStyle.legend
        }}
      >
        <Text as="small" css={{ fontSize }}>
          {title}
        </Text>
      </legend>
      <div {...(fieldsStyle?.wrapper && { css: fieldsStyle.wrapper })}>
        {options.map((option, index) => (
          <label
            key={option.name || option.value}
            css={{
              display: 'grid',
              gridTemplateColumns: '32px minmax(32px,1fr)',
              alignItems: 'start',
              justifyItems: 'center',
              gap: theme.space[3],
              marginTop: index !== 0 ? theme.space[3] : 0,
              cursor: 'pointer',
              ...(option.disabled && {
                opacity: theme.opacity[0],
                cursor: 'not-allowed'
              }),
              ...fieldsStyle.label
            }}
            data-test-radio-fieldset-selection={option.name || option.value}
            htmlFor={option.name}
            {...rest}
          >
            <RadioButton
              readOnly
              checked={option.value === value}
              data-role={`radio-${option.name || option.value}`}
              data-test-radio-button={option.name || option.value}
              disabled={option.disabled}
              id={option.name || option.value}
              value={option.value}
              onChange={(event) => onChange(event, option)}
            />
            <div css={{ display: 'grid', justifySelf: 'start' }}>
              {showValues && (
                <Text as="h4" color="medium">
                  {option.name || option.value}
                </Text>
              )}
              {option.description && (
                <Text
                  as="small"
                  color="medium"
                  css={{
                    marginTop: theme.space[1],
                    fontSize
                  }}
                >
                  {option.description}
                </Text>
              )}
            </div>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

export default RadioFieldset
